import { useNavigate } from 'react-router-dom';
import useWizard from '../../hooks/useWizard';
import useThemePreview from '../../hooks/useThemePreview';
import usePricing from '../../hooks/usePricing';
import { createBrief } from '../../utils/api';
import ProgressBar from './ProgressBar';
import StepBrandCore from './StepBrandCore';
import StepVisuals from './StepVisuals';
import StepScales from './StepScales';
import StepReview from './StepReview';
import LivePreview from '../preview/LivePreview';
import PriceEstimate from '../preview/PriceEstimate';
import Button from '../ui/Button';
import './WizardContainer.css';

export default function WizardContainer() {
  const navigate = useNavigate();
  const wizard = useWizard();
  const { cssVariables } = useThemePreview(wizard.formData);
  const { enabled: pricingEnabled, priceData, tier, showBreakdown } = usePricing(wizard.formData);

  const handleSubmit = async () => {
    if (!wizard.validateCurrentStep()) return;

    wizard.setIsSubmitting(true);
    try {
      const result = await createBrief(wizard.formData);
      navigate(`/brief/${result.id}`);
    } catch (error) {
      wizard.setErrors({ submit: error.message });
    } finally {
      wizard.setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (wizard.currentStep) {
      case 0:
        return (
          <StepBrandCore
            formData={wizard.formData}
            errors={wizard.errors}
            onChange={wizard.updateFormData}
          />
        );
      case 1:
        return (
          <StepVisuals
            formData={wizard.formData}
            errors={wizard.errors}
            onChange={wizard.updateFormData}
          />
        );
      case 2:
        return (
          <StepScales
            formData={wizard.formData}
            errors={wizard.errors}
            onChange={wizard.updateFormData}
          />
        );
      case 3:
        return <StepReview formData={wizard.formData} priceData={priceData} pricingEnabled={pricingEnabled} />;
      default:
        return null;
    }
  };

  return (
    <div className="wizard-container">
      {/* Honeypot field */}
      <input
        type="text"
        name="_hp"
        value={wizard.formData._hp}
        onChange={(e) => wizard.updateFormData('_hp', null, e.target.value)}
        autoComplete="off"
        tabIndex={-1}
        style={{
          position: 'absolute',
          left: '-9999px',
          opacity: 0,
          height: 0,
          width: 0,
          pointerEvents: 'none'
        }}
        aria-hidden="true"
      />

      <div className="wizard-container__main">
        <ProgressBar
          currentStep={wizard.currentStep}
          onStepClick={wizard.goToStep}
        />

        <div className="wizard-container__step">
          {renderStep()}
        </div>

        {wizard.errors.submit && (
          <div className="wizard-container__error" role="alert">
            {wizard.errors.submit}
          </div>
        )}

        <div className="wizard-container__actions">
          {!wizard.isFirstStep && (
            <Button
              variant="secondary"
              onClick={wizard.prevStep}
              disabled={wizard.isSubmitting}
            >
              Back
            </Button>
          )}
          
          <div className="wizard-container__actions-right">
            {wizard.isLastStep ? (
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={wizard.isSubmitting}
              >
                Create Brief Card
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={wizard.nextStep}
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="wizard-container__preview">
        <div className="wizard-container__preview-label">Live Preview</div>
        <LivePreview formData={wizard.formData} styles={cssVariables} />
        
        {pricingEnabled && priceData && (
          <PriceEstimate 
            priceData={priceData} 
            tier={tier} 
            showBreakdown={showBreakdown}
          />
        )}
      </div>
    </div>
  );
}
